import React, { useState } from 'react';
import { CloseIcon } from './Icons';
import { useAuth } from './AuthContext';

// Two modes: 'self' lets the logged-in user change their own password (needs
// the current one as proof); 'admin' lets a superadmin reset another admin's
// password directly (e.g. after a lockout), which needs no current password.
function ChangePasswordModal({ mode = 'self', targetAdmin, onClose }) {
  const { authFetch } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const url =
        mode === 'admin'
          ? `/api/admins/${encodeURIComponent(targetAdmin.id)}/reset-password`
          : '/api/auth/change-password';
      const body = mode === 'admin' ? { newPassword } : { currentPassword, newPassword };

      const response = await authFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update password');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{mode === 'admin' ? `Reset password` : 'Change password'}</h2>
            {mode === 'admin' && <p className="modal-subtitle">for {targetAdmin.username}</p>}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>

        {done ? (
          <div className="modal-body">
            <p className="call-feedback call-feedback-ok">Password updated.</p>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="modal-body" onSubmit={handleSubmit}>
            {mode === 'self' && (
              <label className="login-field">
                <span>Current password</span>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                  autoFocus
                />
              </label>
            )}
            <label className="login-field">
              <span>New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={8}
                required
                autoFocus={mode === 'admin'}
              />
            </label>
            <label className="login-field">
              <span>Confirm new password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
                required
              />
            </label>

            {error && <div className="login-error">{error}</div>}

            <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
              {submitting ? 'Saving…' : mode === 'admin' ? 'Reset password' : 'Change password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ChangePasswordModal;
